import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { StorageService } from '@shared/services/storage.service';
import { Router } from '@angular/router';
import { AuthUser, LoggedInUser, TokensPayload } from '../models/auth.model';
import { environment } from '@environments/environment';
import { jwtDecode } from 'jwt-decode';
import { catchError, Subject, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  appUri = environment.apiUrl;
  authTokens: TokensPayload | null = this.getToken() || null;
  tokenRefreshed = new Subject<void>();

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private router: Router
  ) {}

  signup(userInfo: AuthUser) {
    return this.http.post(`${this.appUri}/account/signup`, userInfo);
  }

  companySignup(companyInfo: AuthUser) {
    return this.http.post(`${this.appUri}/account/signup-organization`, companyInfo);
  }

  resendEmailConfirmation(email:any) {
    return this.http.post(
      `${this.appUri}/account/resend-email-confirmation?email=${email}`, email );
  }

  login(authUser: AuthUser) {
    return this.http.post(
      `${this.appUri}/connect/login?requestedPortal=1`,
      authUser
    );
  }

  getToken() {
    const tokens = this.storageService.getItem('tokens');
    return (tokens ? JSON.parse(tokens) : null) as TokensPayload | null;
  }

  setToken(tokens: TokensPayload) {
    this.authTokens = tokens;
    return this.storageService.setItem('tokens', JSON.stringify(tokens));
  }

  logout(): void {
    localStorage.clear();
    sessionStorage.clear();
    this.router.navigate(['/login']);
  }

  confirmEmail(email: string, token: string) {
    const paramsData = {
      email: encodeURIComponent(email),
      token: encodeURIComponent(token),
    };
    const params: HttpParams = new HttpParams().appendAll(paramsData);
    return this.http.get(`${this.appUri}/account/confirm-email`, { params });
  }

  forgotPassword(email: string) {
    return this.http.post(
      `${this.appUri}/account/forgot-password?requestedPortal=1&email=${email}`,
      email
    );
  }

  resetPassword(email: string, password: string, code: string) {
    const body = {
      email: encodeURIComponent(email),
      password: password,
      confirmPassword: password,
      code: encodeURIComponent(code),
    };
    return this.http.post(`${this.appUri}/account/reset-password`, body);
  }

  refreshToken(access_token: string, refreshToken: string) {
    return this.http
      .post(`${this.appUri}/connect/refresh`, {
        access_Token: access_token,
        refresh_Token: refreshToken,
      })
      .pipe(
        tap(() => {
          this.tokenRefreshed.next();
        }),
        catchError((error) => {
          // 404 - user not found.
          if (error.status === 410 && error.status === 404) {
            this.logout();
          }
          return throwError(() => error);
        })
      );
  }

  twoFactorAuth(email: string, code: string) {
    return this.http.post(`${this.appUri}/connect/verify-2fa`, {
      email,
      code,
    });
  }

  resend2FA(email: string) {
    return this.http.post(
      `${this.appUri}/connect/resend-2fa?email=${email}`,
      {}
    );
  }

  deleteAccount() {
    return this.http.delete(`${this.appUri}/account`);
  }

  reactivateAccount() {
    return this.http.post(`${this.appUri}/account/reactivate`, {});
  }

  getLoggedInProfileData() {
    const token = this.getToken();
    if(!token) return;
    const userProfile = jwtDecode(JSON.stringify(token));
    return userProfile;
  }

  isOrganizationExist() {
    const loggedInProfileData = this.getLoggedInProfileData() as LoggedInUser;
    return loggedInProfileData && loggedInProfileData.OrganizationId;
  }

  getOrganizationId(): string | null {
    const token = this.getToken();
    if (!token) return null;

    const decodedToken: any = jwtDecode(JSON.stringify(token));
    return decodedToken.OrganizationId || null;
  }

  getDefaultProfile() {
    return this.http.get(`${this.appUri}/user-profiles/preview`);
  }

  microsoftAuthLogin(data: any) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'access_token':  data.accessToken
    });

    return this.http.post(`${this.appUri}/external-auth/microsoft`, '' , { headers });
  }

  googleAuthLogin(data: {
    idToken: string;
    accessToken: string;
    email: string;
    name: string;
    picture: string;
  }) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'access_token': data.accessToken
    });

    const body = {
      access_token: data.accessToken,
    };

    return this.http.post(`${this.appUri}/external-auth/google`, body, { headers });
  }

  isLoggedIn() {
    return !!this.storageService.getItem('tokens');
  }

  getUserIdfromToken(): string | null {
    const token = this.getToken();
    if (!token) return null;
    const decodedToken: any = jwtDecode(JSON.stringify(token));
    return decodedToken.UserId || null;
  }

  getUserEmailFromToken(): string | null {
    const token = this.getToken();
    if (!token) return null;
    const decodedToken: any = jwtDecode(JSON.stringify(token));
    return decodedToken.email || null;
  }

}
